import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../utils/apiClient';

export default function AgentConfigEditor({ agentId, onClose, embedded = false }) {
  const [config, setConfig] = useState(null);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadKnowledgeBases = useCallback(async () => {
    try {
      const response = await apiFetch('/api/knowledge-bases');
      if (response.ok) {
        const data = await response.json();
        setKnowledgeBases(data);
      }
    } catch (error) {
      console.error('Failed to load knowledge bases:', error);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch(`/api/agents/${agentId}/config`);
      
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.statusText}`);
      }
      
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Failed to load config:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadConfig();
    loadKnowledgeBases();
  }, [loadConfig, loadKnowledgeBases]);

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await apiFetch(`/api/agents/${agentId}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error(`Failed to save config: ${response.statusText}`);
      }

      const result = await response.json();
      alert(`✓ ${result.message}`);
    } catch (error) {
      console.error('Failed to save config:', error);
      setError(error.message);
      alert(`✗ Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const pollSyncStatus = useCallback(async () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiFetch(`/api/agents/${agentId}/knowledge-base/status`);
        if (!response.ok) return;
        
        const statusData = await response.json();
        
        // Check if sync is complete
        if (statusData.status === 'COMPLETED') {
          clearInterval(pollInterval);
          setSyncing(false);
          alert(`✓ Knowledge base sync completed successfully!\n\nDocuments indexed: Check the status for details.`);
          // Reload config to get updated status
          loadConfig();
        } else if (statusData.status === 'FAILED') {
          clearInterval(pollInterval);
          setSyncing(false);
          alert(`✗ Knowledge base sync failed.\n\nError: ${statusData.error || 'Unknown error'}`);
          loadConfig();
        }
        // If still SYNCING, continue polling
      } catch (error) {
        console.error('Failed to check sync status:', error);
      }
    }, 10000); // Poll every 10 seconds
    
    // Stop polling after 30 minutes (safety timeout)
    setTimeout(() => {
      clearInterval(pollInterval);
      setSyncing(false);
    }, 30 * 60 * 1000);
  }, [agentId, loadConfig]);

  const syncKnowledgeBase = useCallback(async () => {
    try {
      setSyncing(true);
      setError(null);
      
      const response = await apiFetch(`/api/agents/${agentId}/knowledge-base/sync`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to sync: ${response.statusText}`);
      }

      const data = await response.json();
      alert(`✓ Sync started!\n\nJob ID: ${data.job_id}\nRepositories: ${data.repos_to_index}\n\nStatus: ${data.status}`);
      
      // Start polling for status updates
      pollSyncStatus();
    } catch (error) {
      console.error('Failed to sync:', error);
      setError(error.message);
      alert(`✗ Failed to start sync: ${error.message}`);
      setSyncing(false);
    }
  }, [agentId, pollSyncStatus]);

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading configuration...</div>
        </div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-800 font-medium">Error loading configuration</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={loadConfig}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center text-gray-500">Agent not found</div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"}>
      <div className={embedded ? "w-full" : "bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"}>
        <div className="p-6 max-w-4xl mx-auto overflow-y-auto max-h-[calc(90vh-40px)]">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span className="text-3xl">{config.icon}</span>
              <span>{config.name} Configuration</span>
            </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded p-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
          <input
            type="text"
            value={config.name}
            onChange={(e) => setConfig({...config, name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={config.description}
            onChange={(e) => setConfig({...config, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
          <input
            type="text"
            value={config.icon}
            onChange={(e) => setConfig({...config, icon: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="🤖"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">System Prompt</label>
          <textarea
            value={config.system_prompt}
            onChange={(e) => setConfig({...config, system_prompt: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={10}
          />
          <p className="text-xs text-gray-500 mt-1">
            This prompt defines the agent&apos;s behavior and expertise
          </p>
        </div>
      </div>

      {/* Knowledge Base */}
      <div className="mb-6 p-5 border border-gray-300 rounded-lg bg-gray-50">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <span>📚</span>
          <span>Knowledge Base</span>
        </h3>
        
        <div className="mb-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.knowledge_base.enabled}
              onChange={(e) => setConfig({
                ...config,
                knowledge_base: {
                  ...config.knowledge_base,
                  enabled: e.target.checked
                }
              })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">Enable Knowledge Base</span>
          </label>
        </div>

        {config.knowledge_base.enabled && (
          <div className="space-y-4 ml-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Knowledge Bases
                <span className="text-red-500 ml-1">*</span>
                <span className="text-gray-500 font-normal ml-2">(select multiple)</span>
              </label>
              <div className="border border-gray-300 rounded p-3 max-h-64 overflow-y-auto space-y-2">
                {knowledgeBases.length === 0 ? (
                  <p className="text-sm text-gray-500">No knowledge bases available. Register one in Settings → Knowledge Bases.</p>
                ) : (
                  knowledgeBases.map((kb) => {
                    const isSelected = (config.knowledge_base.kb_ids || [config.knowledge_base.kb_id].filter(Boolean)).includes(kb.id);
                    return (
                      <label key={kb.id} className={`flex items-start p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const currentIds = config.knowledge_base.kb_ids || [config.knowledge_base.kb_id].filter(Boolean);
                            const newIds = e.target.checked
                              ? [...currentIds, kb.id]
                              : currentIds.filter(id => id !== kb.id);
                            setConfig({
                              ...config,
                              knowledge_base: {
                                ...config.knowledge_base,
                                kb_ids: newIds,
                                kb_id: newIds[0] || null // Keep first as primary for backward compatibility
                              }
                            });
                          }}
                          className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{kb.name}</span>
                            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{kb.id}</span>
                            <span className="text-xs text-gray-500">🗂️ {kb.data_sources.length} sources</span>
                          </div>
                          {kb.description && (
                            <p className="text-xs text-gray-600 mt-1">{kb.description}</p>
                          )}
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select one or more Knowledge Bases. Each KB will be queried for relevant information. Configure KB settings in Settings → Knowledge Bases.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={syncKnowledgeBase}
                disabled={syncing}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {syncing ? (
                  <>
                    <span className="animate-spin">⟳</span>
                    <span>Syncing...</span>
                  </>
                ) : (
                  <>
                    <span>🔄</span>
                    <span>Sync Knowledge Base</span>
                  </>
                )}
              </button>
              <span className="text-xs text-gray-500">
                Starts an immediate sync job for the selected knowledge bases.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* MCP Tools */}
      <div className="mb-6 p-5 border border-gray-300 rounded-lg bg-gray-50">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <span>🔧</span>
          <span>MCP Tools (HTTP Servers)</span>
        </h3>
        
        <div className="space-y-3 mb-4">
          {(config.mcp_tools || []).map((tool, index) => (
            <div key={index} className="p-4 bg-white rounded border border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={tool.enabled}
                    onChange={(e) => {
                      const newTools = [...config.mcp_tools];
                      newTools[index].enabled = e.target.checked;
                      setConfig({...config, mcp_tools: newTools});
                    }}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={tool.name}
                      onChange={(e) => {
                        const newTools = [...config.mcp_tools];
                        newTools[index].name = e.target.value;
                        setConfig({...config, mcp_tools: newTools});
                      }}
                      placeholder="Tool name (e.g., forms-mcp)"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-medium focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  onClick={() => {
                    const newTools = config.mcp_tools.filter((_, i) => i !== index);
                    setConfig({...config, mcp_tools: newTools});
                  }}
                  className="ml-2 px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                >
                  Remove
                </button>
              </div>
              <div className="ml-7 space-y-3">
                <div>
                  <input
                    type="text"
                    value={tool.url || ''}
                    onChange={(e) => {
                      const newTools = [...config.mcp_tools];
                      newTools[index].url = e.target.value;
                      setConfig({...config, mcp_tools: newTools});
                    }}
                    placeholder="https://your-mcp-server.example.com"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">MCP server HTTP endpoint</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    HTTP Headers (JSON format)
                  </label>
                  <textarea
                    value={tool.config?.headers ? JSON.stringify(tool.config.headers, null, 2) : '{}'}
                    onChange={(e) => {
                      try {
                        const headers = JSON.parse(e.target.value);
                        const newTools = [...config.mcp_tools];
                        if (!newTools[index].config) {
                          newTools[index].config = {};
                        }
                        newTools[index].config.headers = headers;
                        setConfig({...config, mcp_tools: newTools});
                      } catch (err) {
                        console.warn('Invalid MCP header JSON', err);
                        // Invalid JSON, update the raw value but don't parse
                        const newTools = [...config.mcp_tools];
                        if (!newTools[index].config) {
                          newTools[index].config = {};
                        }
                        newTools[index].config.headersRaw = e.target.value;
                        setConfig({...config, mcp_tools: newTools});
                      }
                    }}
                    placeholder='{\n  "X-API-Key": "your-api-key"\n}'
                    rows={3}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-xs font-mono focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add custom headers (e.g., API keys). Example: {`{"X-API-Key": "your-api-key-here"}`}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => {
            const newTool = {
              name: 'my-mcp-server',
              type: 'http',
              url: 'https://your-mcp-server.example.com',
              enabled: true,
              config: {
                headers: {
                  "X-API-Key": "your-api-key-here"
                }
              }
            };
            setConfig({...config, mcp_tools: [...(config.mcp_tools || []), newTool]});
          }}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
        >
          <span>➕</span>
          <span>Add MCP Server</span>
        </button>

        <p className="text-xs text-gray-500 mt-3">
          Add HTTP-based MCP servers that provide tools for your agent. Each server should expose MCP-compatible endpoints. Use headers for authentication (e.g., API keys).
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-4 border-t border-gray-200">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
        >
          {saving ? (
            <>
              <span className="animate-spin">⟳</span>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>Save Configuration</span>
            </>
          )}
        </button>
        
        <button
          onClick={loadConfig}
          disabled={loading}
          className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span>↺</span>
          <span>Reset Changes</span>
        </button>
      </div>

      {/* Metadata */}
      <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
        <div>Created: {new Date(config.created_at).toLocaleString()}</div>
        <div>Updated: {new Date(config.updated_at).toLocaleString()}</div>
      </div>
        </div>
      </div>
    </div>
  );
}

AgentConfigEditor.propTypes = {
  agentId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  embedded: PropTypes.bool
};
